/// <reference types="@angular/localize" />

import * as Sentry from '@sentry/angular';
import { provideKeycloak } from 'keycloak-angular';
import { getEnvironment } from 'src/environments/environment';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAT_TABS_CONFIG, MatTabsModule } from '@angular/material/tabs';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { provideHttpClient } from '@angular/common/http';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule } from '@angular/material/tooltip';
import { ErrorHandler, importProvidersFrom, inject } from '@angular/core';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';
import { MatRadioModule } from '@angular/material/radio';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TextFieldModule } from '@angular/cdk/text-field';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MarkdownModule, MARKED_OPTIONS, MarkedOptions, CLIPBOARD_OPTIONS } from 'ngx-markdown';
import { marked, Tokens } from 'marked';
import { ClipboardButtonComponent } from './app/components/chat/clipboard-button/clipboard-button.component';
import { AppComponent } from './app/app.component';
import { InMemoryCache } from 'node_modules/@apollo/client/cache/inmemory/inMemoryCache';
import { createClient } from 'graphql-ws';
import { provideApollo } from 'apollo-angular';
import Keycloak from 'keycloak-js';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { ConnectionService } from './app/services/connection.service';
import { provideRouter } from '@angular/router';
import { routes } from './app/app.routes';


bootstrapApplication(AppComponent, {
    providers: [
        provideRouter(routes),
        importProvidersFrom(
            BrowserModule,
            MatSidenavModule,
            MatListModule,
            MatCardModule,
            MatMenuModule,
            MatButtonModule,
            MatTabsModule,
            MatButtonToggleModule,
            MatFormFieldModule,
            ReactiveFormsModule,
            MatInputModule,
            MatPaginatorModule,
            MatCheckboxModule,
            MatTableModule,
            MatRadioModule,
            MatExpansionModule,
            MatOptionModule,
            MatSelectModule,
            MatDialogModule,
            MatIconModule,
            MatProgressSpinnerModule,
            TextFieldModule,
            MatTooltipModule,
            ScrollingModule,
            ClipboardModule,
            MarkdownModule.forRoot({
                markedOptions: {
                    provide: MARKED_OPTIONS,
                    useFactory: (): MarkedOptions => {
                        const renderer = new marked.Renderer();
                        const linkRenderer = renderer.link;
                        // Override link rendering so all markdown links open in a new tab
                        // instead of navigating away from the app.
                        renderer.link = function(link: Tokens.Link): string {
                            const html = linkRenderer.call(this, link);
                            return html.replace(/^<a /, '<a target="_blank" rel="nofollow" ');
                        };
                        return { renderer };
                    },
                },
                clipboardOptions: {
                    provide: CLIPBOARD_OPTIONS,
                    useValue: {
                        buttonComponent: ClipboardButtonComponent,
                    },
                },
            }),
        ),
        provideKeycloak({
            config: getEnvironment().keycloak,
            initOptions: {
                onLoad: 'check-sso',
                silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
            },
        }),
        provideAnimationsAsync(),
        { provide: MAT_TABS_CONFIG, useValue: { animationDuration: '0ms' } },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always', subscriptSizing: 'dynamic' } },
        { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: -1 } },
        provideHttpClient(),
        { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: { showDelay: 300, tooltipClass: 'tooltip-container' } },
        // Provider below will not do anything if Sentry is not initialized
        { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
        provideApollo(() => {
            // Keycloak
            const keycloak = inject(Keycloak);

            const connectionService = inject(ConnectionService);

            // Define websocket link with hasura endpoint
            const link = new GraphQLWsLink(
                createClient({
                    // Get hasura endpoint from environment file
                    url: getEnvironment().graphqlws,
                    lazy: false,
                    retryAttempts: 3,

                    // Logic that return the required connection headers for hasura
                    connectionParams: async () => {
                        // Check current authentication status
                        const authenticated = keycloak.authenticated;
                        if (authenticated) {
                            // Always update token
                            await keycloak.updateToken();

                            // Find roles user has acces to and choose role that grants the most features
                            const allowedRoles = keycloak.tokenParsed!['https://hasura.io/jwt/claims']?.['x-hasura-allowed-roles'] as string[];

                            // Determine best role for user. Use 'admin' if available and otherwise the first allowed role
                            const role = allowedRoles.includes('admin') ? 'admin' : allowedRoles[0];

                            connectionService.state.next('authenticated');

                            return {
                                headers: {
                                    Authorization: `Bearer ${keycloak.token}`,
                                    // Add desired role
                                    ...(role && { 'X-Hasura-Role': role }),
                                },
                            };
                        }
                        // If user is not logged in yet, start keycloak login flow
                        keycloak.login();
                        return undefined;
                    },
                }),

            );

            // Return ApolloClientOptions
            return {
                link,
                cache: new InMemoryCache({}),
                // Disable cache on queries by default
                defaultOptions: {
                    query: {
                        fetchPolicy: 'no-cache',
                    },
                },
            };
        }),
    ],
}).catch((err: unknown) => console.error(err));